import React, { useRef, useEffect } from "react";
import { Link } from "react-router-dom";
import { RightCarrotIcon } from "../../Icons";
import { NavGroupProps } from "./types";
import { Tooltip } from "../../Tooltip";

// Define a functional component called NavGroup that takes in several props
export const NavGroup: React.FC<NavGroupProps> = (
  { isOpen = false, href = "#", onClick = () => null, name, icon, items = [], badge, type = "default" },
  key
) => {
  // Create a ref to the list element
  const listRef = useRef(null);

  // Define a function that checks if the current path matches the href prop
  const getIsActive = () => {
    const currentPath = window.location.pathname;
    const isActive = currentPath === href;
    return isActive;
  };

  // Use an effect to set the height of the parent element based on whether the NavGroup is open or not
  useEffect(() => {
    if (listRef.current) {
      const height = isOpen ? listRef.current.scrollHeight : 0;
      listRef.current.parentElement.style.height = `${height}px`;
    }
  }, [isOpen]);

  // Return a JSX element that renders a list item with several nested elements
  return (
    <li
      data-testid="NavGroup"
      key={key}
      className="menu nav-item relative group"
      // remove default title attr
    >
      <Link
        to={!items.length && href}
        onClick={Boolean(items.length) ? onClick : () => null}
        className={`nav-link group text-black dark:text-white cursor-pointer flex p-1 ${
          Boolean(isOpen) && "active"
        }`}
        tabIndex={0}
      >
        <div
          className={`text-black/50 dark:text-white/20 h-5 flex items-center justify-center transition-all duration-200 ${
            Boolean(isOpen) && "!rotate-90"
          }`}
        >
          {/* Render a RightCarrotIcon if the NavGroup has items */}
          {items.length ? <RightCarrotIcon /> : null}
        </div>
          <Tooltip
            content={name || ""}
            disabled={type === "default" || !name}
            offset={20}
          >
        <div>
          <span
            className={`nested-link ${getIsActive() && "active font-semibold"}`}
          >
              <div className="flex items-center">
                {/* Render the icon prop (with optional numeric badge) */}
                <span className="relative inline-flex items-center justify-center">
                  {icon}
                  {Boolean(badge) && badge > 0 && (
                    <span className="pointer-events-none absolute -right-0.5 top-0.5 flex h-[14px] min-w-[14px] -translate-y-1/2 translate-x-1/2 items-center justify-center rounded-full bg-red-500 px-1 text-[8px] font-bold leading-none text-white ring-2 ring-white">
                      {badge > 9 ? "9+" : badge}
                    </span>
                  )}
                </span>
                {/* Render the name prop */}
                {type === "default" && <span className="pl-2">{name}</span>}
              </div>
          </span>
        </div>
            </Tooltip>
      </Link>

      {/* Render a sub-menu if the NavGroup has items */}
      {Boolean(items.length) && isOpen && (
        <ul
          data-testid="NavGroupSubMenu"
          className={`overflow-hidden transition-all duration-300 sub-menu flex flex-col gap-1 text-black dark:text-white/80`}
        >
          <div
            ref={listRef}
            className={`${isOpen ? "opacity-100" : "opacity-0"} grid gap-1`}
          >
            {/* Render each item in the items prop */}
            {Boolean(items.length) &&
              items.map(({ href, label, icon, isActive = false }, key) => (
                <li key={key}>
                  <Tooltip
                    content={label || ""}
                    disabled={type === "default" || !label}
                    offset={20}
                  >
                    <Link to={href} className={isActive ? "active" : ""}>
                      {/* Render the icon prop */}
                      <span>{icon}</span>
                      {/* Render the label prop */}
                      {type === "default" && label}
                    </Link>
                  </Tooltip>
                </li>
              ))}
          </div>
        </ul>
      )}
    </li>
  );
};
